import React, { useContext, useMemo, useReducer } from "react";
import Draft from "draft-js";

export interface SearchTermState {
  isSelectionInSearchTerm: boolean;
  searchTerm: string | null;
  searchTermLength: number;
  startingSelection: Draft.SelectionState | null;
  position: DOMRect | null;
  entityKey: string | null;
}

export const defaultSearchTermState: SearchTermState = {
  isSelectionInSearchTerm: false,
  searchTerm: null,
  searchTermLength: 0,
  startingSelection: null,
  position: null,
  entityKey: null,
};

// Actions
export enum ActionType {
  CancelSearchTerm = "CANCEL_SEARCH_TERM",
  StartSearchTerm = "START_SEARCH_TERM",
  IncrementSearchTermLength = "INCREMENT_SEARCH_TERM_LENGTH",
  DecrementSearchTermLength = "DECREMENT_SEARCH_TERM_LENGTH",
  UpdateSearchTerm = "UPDATE_SEARCH_TERM",
  UpdateSearchTermPosition = "UPDATE_SEARCH_TERM_POSITION",
  UpdateSearchTermEntityKey = "UPDATE_SEARCH_TERM_ENTITY_KEY",
}

export interface CancelSearchTerm {
  type: ActionType.CancelSearchTerm;
}
export const cancelSearchTermAction = (): CancelSearchTerm => ({
  type: ActionType.CancelSearchTerm,
});

export interface StartSearchTerm {
  type: ActionType.StartSearchTerm;
  payload: Draft.EditorState;
}
export const startSearchTermAction = (
  payload: Draft.EditorState,
): StartSearchTerm => ({
  type: ActionType.StartSearchTerm,
  payload,
});

export interface UpdateSearchTerm {
  type: ActionType.UpdateSearchTerm;
  payload: Draft.EditorState;
}
export const updateSearchTermAction = (
  payload: Draft.EditorState,
): UpdateSearchTerm => ({
  type: ActionType.UpdateSearchTerm,
  payload,
});

export interface UpdateSearchTermPosition {
  type: ActionType.UpdateSearchTermPosition;
  payload: DOMRect;
}
export const updateSearchTermPositionAction = (
  payload: DOMRect,
): UpdateSearchTermPosition => ({
  type: ActionType.UpdateSearchTermPosition,
  payload,
});

export interface UpdateSearchTermEntityKey {
  type: ActionType.UpdateSearchTermEntityKey;
  payload: string;
}
export const updateSearchTermEntityKeyAction = (
  entityKey: string,
): UpdateSearchTermEntityKey => ({
  type: ActionType.UpdateSearchTermEntityKey,
  payload: entityKey,
});

export interface IncrementSearchTermLength {
  type: ActionType.IncrementSearchTermLength;
  payload: Draft.EditorState;
}
export const incrementSearchTermLengthAction = (
  payload: Draft.EditorState,
): IncrementSearchTermLength => ({
  type: ActionType.IncrementSearchTermLength,
  payload,
});

export interface DecrementSearchTermLength {
  type: ActionType.DecrementSearchTermLength;
  payload: {
    editorState: Draft.EditorState;
    decrementAmount: number;
  };
}
export const decrementSearchTermLengthAction = (
  editorState: Draft.EditorState,
  decrementAmount: number,
): DecrementSearchTermLength => ({
  type: ActionType.DecrementSearchTermLength,
  payload: {
    editorState,
    decrementAmount,
  },
});

// Helpers
export const getNextSearchTermState = (
  editorState: Draft.EditorState,
  searchTermState: SearchTermState,
) => {
  const { searchTermLength, startingSelection, position, entityKey } =
    searchTermState;
  if (!startingSelection) return defaultSearchTermState;

  const currentSelection = editorState.getSelection();
  const currentContent = editorState.getCurrentContent();
  const selectionStartBlock = currentSelection.getStartKey();
  const selectionEndBlock = currentSelection.getEndKey();
  const searchTermStartBlock = startingSelection.getStartKey();
  const searchTermEndBlock = startingSelection.getEndKey();
  const selectionStart = currentSelection.getStartOffset();
  const selectionEnd = currentSelection.getEndOffset();
  const searchTermStart = startingSelection.getStartOffset();
  const searchTermEnd = startingSelection.getStartOffset() + searchTermLength;
  const isAfterAtSymbol = selectionStart >= searchTermStart;
  const isBeforeLastCharacter = selectionEnd <= searchTermEnd;

  const nextIsSelectionInSearchTermBlock =
    selectionStartBlock === searchTermStartBlock ||
    selectionEndBlock === searchTermEndBlock;

  const nextIsSelectionInSearchTerm =
    nextIsSelectionInSearchTermBlock &&
    isAfterAtSymbol &&
    isBeforeLastCharacter;

  const nextSearchTerm = currentContent
    .getBlockForKey(searchTermStartBlock)
    .getText()
    .substr(searchTermStart, searchTermLength);

  return {
    searchTerm: nextSearchTerm,
    isSelectionInSearchTerm: nextIsSelectionInSearchTerm,
    searchTermLength,
    startingSelection,
    position,
    entityKey,
  };
};

// Reducer
export type AllSearchTermActions =
  | CancelSearchTerm
  | StartSearchTerm
  | IncrementSearchTermLength
  | DecrementSearchTermLength
  | UpdateSearchTerm
  | UpdateSearchTermPosition
  | UpdateSearchTermEntityKey;

export type SearchTermActionCreator = (
  editorState: Draft.EditorState,
) => AllSearchTermActions;

export const searchTermReducer = (
  state = defaultSearchTermState,
  action: AllSearchTermActions,
): SearchTermState => {
  switch (action.type) {
    case ActionType.CancelSearchTerm: {
      return defaultSearchTermState;
    }
    case ActionType.StartSearchTerm: {
      return {
        isSelectionInSearchTerm: true,
        searchTerm: "@",
        searchTermLength: 1,
        startingSelection: action.payload.getSelection(),
        position: state.position,
        entityKey: null,
      };
    }
    case ActionType.UpdateSearchTerm: {
      return getNextSearchTermState(action.payload, state);
    }
    case ActionType.UpdateSearchTermPosition: {
      return { ...state, position: action.payload };
    }
    case ActionType.UpdateSearchTermEntityKey: {
      return { ...state, entityKey: action.payload };
    }
    case ActionType.IncrementSearchTermLength: {
      // Increment search term length, and regenerate search term state
      return getNextSearchTermState(action.payload, {
        ...state,
        searchTermLength: state.searchTermLength + 1,
      });
    }
    case ActionType.DecrementSearchTermLength: {
      const { editorState, decrementAmount } = action.payload;
      // Decrement search term length, and regenerate search term state
      const nextSearchTermState = getNextSearchTermState(editorState, {
        ...state,
        searchTermLength: state.searchTermLength - decrementAmount,
      });
      if (
        // Cancel search term tracking when user deletes the entire search term
        nextSearchTermState.searchTermLength <= 0 ||
        // Cancel search term when selection leaves the search term range
        !nextSearchTermState.isSelectionInSearchTerm
      )
        return defaultSearchTermState;

      return nextSearchTermState;
    }
    default: {
      return state;
    }
  }
};

// Context
export interface SearchTermStoreContext {
  state: SearchTermState;
  dispatch: React.Dispatch<AllSearchTermActions>;
}
export const defaultSearchTermContext: SearchTermStoreContext = {
  state: defaultSearchTermState,
  dispatch: () => undefined,
};
export const SearchTermStoreContext = React.createContext(
  defaultSearchTermContext,
);
export const useSearchTermStore = () => useContext(SearchTermStoreContext);

// Provider
export interface SearchTermStoreProviderProps {
  children?: React.ReactNode;
  initialState: SearchTermState;
}
export const SearchTermStoreProvider = ({
  children,
  initialState,
}: SearchTermStoreProviderProps) => {
  const [state, dispatch] = useReducer(searchTermReducer, initialState);
  return useMemo(
    () => (
      <SearchTermStoreContext.Provider value={{ state, dispatch }}>
        {children}
      </SearchTermStoreContext.Provider>
    ),
    [children, state],
  );
};
